import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useConfig } from '../contexts/ConfigContext'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const { config } = useConfig()
  const location = useLocation()

  const navigation = [
    { name: '首页', href: '/' },
    { name: '产品介绍', href: '/products' },
    { name: '公司简介', href: '/company' },
    { name: '技术支持', href: '/downloads' },
  ]

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-tech-dark/95 backdrop-blur-md shadow-tech border-b border-tech-neon/20' 
        : 'bg-gradient-to-r from-tech-dark/80 to-tech/80 backdrop-blur-md'
    }`}>
      <nav className="container-max">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-4 group">
            <div className="flex items-center justify-center shadow-tech-glow group-hover:animate-glow transition-all duration-300 overflow-hidden bg-white rounded-xl">
              {(isMobile ? config?.identity?.logo_mobile : config?.identity?.logo_pc) ? (
                <img 
                  src={isMobile ? config.identity.logo_mobile : config.identity.logo_pc} 
                  alt={config?.identity?.site_name || '摩恒科技'} 
                  className={`max-h-[${config?.identity?.logo_height || 200}px] max-w-[${config?.identity?.logo_width || 430}px] object-contain p-1`}
                  style={{ 
                    maxWidth: `${config?.identity?.logo_width || 430}px`, 
                    maxHeight: `${config?.identity?.logo_height || 200}px` 
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/api/placeholder/48/48';
                  }}
                />
              ) : (
                <div className="w-12 h-12 bg-gradient-to-br from-tech-neon to-tech-accent flex items-center justify-center">
                  <span className="text-white font-bold text-xl">M</span>
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <span className={`font-bold text-xl transition-all duration-300 ${
                isScrolled ? 'text-white' : 'text-white'
              } group-hover:text-tech-neon`}>
                {config?.identity?.site_name || '摩恒科技'}
              </span>
              <span className={`text-xs transition-all duration-300 ${
                isScrolled ? 'text-tech-neon/80' : 'text-tech-neon/90'
              } group-hover:text-tech-glow`}>
                {config?.identity?.site_name?.includes('无锡市摩恒科技有限公司') 
                  ? 'Wuxi Moheng Technology Co., Ltd' 
                  : config?.identity?.site_name || 'Wuxi Moheng Technology Co., Ltd'}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`relative px-6 py-3 font-medium transition-all duration-300 rounded-lg ${
                  isActive(item.href)
                    ? 'text-white bg-tech-neon/20 shadow-tech border border-tech-neon/30'
                    : isScrolled
                    ? 'text-gray-300 hover:text-white hover:bg-tech/50 hover:shadow-tech'
                    : 'text-gray-200 hover:text-white hover:bg-tech/40 hover:shadow-tech'
                } group`}
              >
                {item.name}
                {isActive(item.href) && (
                  <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-0.5 bg-tech-neon rounded-full animate-glow"></span>
                )}
                <span className="absolute inset-0 rounded-lg bg-gradient-to-r from-transparent via-tech-neon/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              </Link>
            ))}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden p-3 rounded-xl transition-all duration-300 ${
              isScrolled 
                ? 'bg-tech/50 text-tech-neon hover:bg-tech/70 hover:shadow-tech' 
                : 'bg-tech/30 text-white hover:bg-tech/50 hover:shadow-tech'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-tech-dark/95 backdrop-blur-md border border-tech-neon/20 rounded-xl mt-2 py-2 shadow-tech animate-slide-in">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-6 py-3 font-medium transition-all duration-300 mx-2 my-1 rounded-lg ${
                  isActive(item.href)
                    ? 'text-white bg-tech-neon/20 border border-tech-neon/30 shadow-tech'
                    : 'text-gray-300 hover:text-white hover:bg-tech/50 hover:shadow-tech'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        )}
      </nav>
    </header>
  )
}

export default Header
